"use client";
import { z } from "zod";
import { useState } from "react";
import axios from "@config/axios";
import { AxiosError } from "axios";
import useSession from "@stores/session";
import { useForm } from "react-hook-form";
import useCounter from "@hooks/useCounter";
import { toast } from "@components/ui/toaster";
import { Button } from "@components/ui/button";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { RefreshCcw, ShieldCheck } from "lucide-react";
import { VerificationSchema } from "@schemas/verification";
import { useRouter, useSearchParams } from "next/navigation";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@components/ui/input-otp";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@components/ui/form";

type VerifyFormProps = {
	email: string;
	type: string;
};

const VerifyForm = ({ email, type }: VerifyFormProps) => {
	const router = useRouter();
	const { setSession } = useSession();
	const searchParams = useSearchParams();
	const urlParams = new URLSearchParams(searchParams?.toString() || "");
	const { counter, startCounter } = useCounter(59);
	const [onResendToast, setOnResendToast] = useState<string | number>();
	const [onVerifyToast, setOnVerifyToast] = useState<string | number>();

	const defaultValues: z.infer<typeof VerificationSchema> = {
		otp: "",
	};

	const form = useForm<z.infer<typeof VerificationSchema>>({
		defaultValues: defaultValues,
		mode: "onTouched",
		resolver: zodResolver(VerificationSchema),
	});

	const onResend = async () => {
		setOnResendToast(toast.loading("Loading...", { description: "Resending OTP..." }));
		startCounter(59);
		await axios.post("/api/otp/get-otp", { email });
	};

	const { mutate: resendMutate, isPending: resendIsPending } = useMutation({
		mutationFn: onResend,
		onSuccess: () => {
			toast.success("Success!", { id: onResendToast, description: "OTP sent successfully!" });
		},
		onError: (error: unknown) => {
			toast.error("Error!", { id: onResendToast, description: (error as AxiosError<string>)?.response?.data || "Internal server error!" });
		},
	});

	const onVerify = async (values: z.infer<typeof VerificationSchema>) => {
		setOnVerifyToast(toast.loading("Loading...", { description: "Verifying OTP..." }));
		const { otp } = values;
		const response = await axios.post("/api/otp/verify-otp", { email, otp, type });
		if (type === "VERIFICATION") {
			return response.data;
		} else {
			return values;
		}
	};

	const { mutate: verifyMutate, isPending: verifyIsPending } = useMutation({
		mutationFn: onVerify,
		onSuccess: (values: Session | z.infer<typeof VerificationSchema>) => {
			if (type === "VERIFICATION") {
				const session = values as Session;
				setSession(session);
				toast.success("Success!", { id: onVerifyToast, description: "Logged in successfully!" });
				const callbackUrl = urlParams.get("callbackUrl");
				urlParams.delete("callbackUrl");
				if (callbackUrl) {
					router.replace(`${callbackUrl}${urlParams ? `?${urlParams.toString()}` : ""}`);
				} else {
					router.push(`/dashboard${urlParams ? `?${urlParams.toString()}` : ""}`);
				}
			} else {
				const { otp } = values as z.infer<typeof VerificationSchema>;
				toast.success("Success!", { id: onVerifyToast, description: "OTP verified successfully!" });
				urlParams.set("email", email);
				urlParams.set("otp", otp);
				router.replace(`/auth/reset-password${urlParams ? `?${urlParams.toString()}` : ""}`);
			}
		},
		onError: (error: unknown) => {
			toast.error("Error!", { id: onVerifyToast, description: (error as AxiosError<string>)?.response?.data || "Internal server error!" });
		},
	});

	return (
		<Form {...form}>
			<form className="flex w-full max-w-sm flex-col items-center justify-stretch gap-7" onSubmit={form.handleSubmit(values => verifyMutate(values))}>
				<div className="flex w-full flex-col items-center justify-center gap-6">
					<FormField
						control={form.control}
						name="otp"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									One-Time Password<span className="text-destructive">*</span>
								</FormLabel>
								<FormControl>
									<InputOTP className="w-full" maxLength={6} {...field}>
										{new Array(6).fill(0).map((_, index) => (
											<InputOTPGroup key={index}>
												<InputOTPSlot index={index} />
											</InputOTPGroup>
										))}
									</InputOTP>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>
				<div className="flex w-full flex-col items-center justify-between gap-3">
					<Button className="w-36 justify-between" disabled={resendIsPending || verifyIsPending} size="lg" type="submit" variant="default">
						Verify
						<ShieldCheck size={16} />
					</Button>
					{counter > 0 ? (
						<p className="flex h-12 w-full items-center justify-center">{`Resend OTP in ${counter} seconds`}</p>
					) : (
						<Button className="w-36" disabled={resendIsPending || verifyIsPending} onClick={() => resendMutate()} size="lg" type="button" variant="ghost">
							<RefreshCcw size={16} />
							Resend OTP
						</Button>
					)}
				</div>
			</form>
		</Form>
	);
};

export default VerifyForm;
